/** @jsx jsx */
import { jsx } from '@emotion/react';

import * as React from 'react';
import { forwardRef, useCallback, useLayoutEffect, useRef, useState } from 'react';

import { FormattedMessage } from 'js/lib/coursera.react-intl';

import { TextField } from '@coursera/cds-core';
import type { TextFieldProps } from '@coursera/cds-core';

import { commonStyles } from 'bundles/authentication/shared/commonStyles';
import useMailcheck from 'bundles/authentication/shared/hooks/useMailcheck';
import TrackedButton from 'bundles/page/components/TrackedButton';

import _t from 'i18n!nls/authentication';

type OwnProps = {
  defaultValue?: string;
  name?: string;
  onValueChange?: (value: string) => void;
  placeholder?: string;
  value?: string;
  'data-e2e'?: string;
};

type Props = OwnProps & Omit<TextFieldProps, keyof OwnProps>;

const EmailInput = forwardRef<HTMLInputElement, Props>(
  (
    {
      label = _t('Email'),
      name = 'email',
      placeholder = _t('name@email.com'),
      onBlur,
      onChange,
      onFocus,
      ...props
    }: Props,
    ref
  ) => {
    const isControlledRef = useRef<boolean>(props.value !== undefined);
    const [state, setState] = useState(props.value ?? props.defaultValue ?? '');

    const value = (isControlledRef.current ? props.value : state) ?? '';

    const suggestion = useMailcheck(value.toString());

    const [showSuggestion, setShowSuggestion] = useState(false);

    const acceptSuggestion = useCallback<() => $TSFixMe>(() => {
      const [username] = value.split('@');

      const email = `${username}@${suggestion?.domain}`;

      if (!isControlledRef.current) {
        setState(email);
      }

      // We mimic sending an input event when we change the email programmatically
      // Note that this isn't a complete event and that it's not typesafe due to: `as unknown as`
      const event = new Event('input') as unknown as React.ChangeEvent<HTMLInputElement>;
      Object.defineProperty(event, 'target', { value: { value: email }, writable: true });

      onChange?.(event);
    }, [onChange, suggestion, value]);

    const handleBlur = useCallback<(...args: $TSFixMe[]) => $TSFixMe>(
      (event: React.FocusEvent<HTMLInputElement>) => {
        setShowSuggestion(true);

        onBlur?.(event);
      },
      [onBlur]
    );

    const handleChange = useCallback<(...args: $TSFixMe[]) => $TSFixMe>(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!isControlledRef.current) {
          setState(event.target.value);
        }

        onChange?.(event);
      },
      [onChange]
    );

    const handleFocus = useCallback<(...args: $TSFixMe[]) => $TSFixMe>(
      (event: React.FocusEvent<HTMLInputElement>) => {
        setShowSuggestion(false);

        onFocus?.(event);
      },
      [onFocus]
    );

    useLayoutEffect(() => {
      isControlledRef.current = props.value !== undefined;
    }, [props.value]);

    return (
      <div>
        <TextField
          inputProps={{ 'data-e2e': props?.['data-e2e'] }}
          fullWidth
          label={label}
          name={name}
          onBlur={handleBlur}
          onChange={handleChange}
          onFocus={handleFocus}
          placeholder={placeholder}
          ref={ref}
          type="email"
          autoComplete="email"
          value={isControlledRef.current ? props.value ?? '' : state}
          {...{ ...props, 'data-e2e': undefined }}
        />
        {showSuggestion && suggestion && (
          <div css={commonStyles.hint}>
            <span className="email-hint">
              <FormattedMessage
                message={_t('Did you mean {suggestedEmailDomain}?')}
                suggestedEmailDomain={
                  <TrackedButton className="nostyle" trackingName="sign_up_email_suggestion" onClick={acceptSuggestion}>
                    @{suggestion.domain}
                  </TrackedButton>
                }
              />
            </span>
          </div>
        )}
      </div>
    );
  }
);

export default EmailInput;
